import { z } from 'zod';
import { localizedString } from './text';

export const fieldTypeSchema = z.enum(['TEXT', 'NUMBER', 'BOOLEAN', 'DATE', 'DATETIME', 'SELECT', 'MULTI_SELECT', 'TEXTAREA', 'QUANTITY', 'REFERENCE']);
export type FieldType = z.infer<typeof fieldTypeSchema>;

export const fieldValidationSchema = z.object({
  min: z.number().optional(),
  max: z.number().optional(),
  minLength: z.number().int().nonnegative().optional(),
  maxLength: z.number().int().nonnegative().optional(),
  /** ECMAScript regular expression source. */
  pattern: z.string().optional(),
});
export type FieldValidation = z.infer<typeof fieldValidationSchema>;

export const fieldOptionSchema = z.object({ value: z.string(), label: localizedString });
export type FieldOption = z.infer<typeof fieldOptionSchema>;

export const fieldUiHintsSchema = z.object({
  mono: z.boolean().optional(),
  placeholder: localizedString.optional(),
  /** Column span inside the section grid (1..4). */
  columns: z.number().int().positive().optional(),
  section: z.string().optional(),
  /** Unit of measure shown next to QUANTITY fields. */
  uom: z.string().optional(),
  helpText: localizedString.optional(),
});
export type FieldUiHints = z.infer<typeof fieldUiHintsSchema>;

export const formFieldSchema = z.object({
  code: z.string().min(1),
  label: localizedString,
  type: fieldTypeSchema,
  required: z.boolean().optional(),
  validation: fieldValidationSchema.optional(),
  options: z.array(fieldOptionSchema).optional(),
  /** Permission required to see and submit the field. */
  permission: z.string().optional(),
  uiHints: fieldUiHintsSchema.optional(),
  defaultValue: z.unknown().optional(),
});
export type FormField = z.infer<typeof formFieldSchema>;

export const formActionSchema = z.object({
  code: z.string().min(1),
  label: localizedString,
  variant: z.enum(['primary', 'secondary', 'danger']).optional(),
  /** `true` or a confirmation message: the action needs a second click. */
  confirm: z.union([z.boolean(), z.string()]).optional(),
});
export type FormAction = z.infer<typeof formActionSchema>;

export const formDefinitionSchema = z
  .object({
    key: z.string().min(1),
    title: localizedString.optional(),
    fields: z.array(formFieldSchema),
    actions: z.array(formActionSchema),
  })
  .superRefine((def, ctx) => {
    const codes = new Set<string>();
    def.fields.forEach((f, index) => {
      if (codes.has(f.code)) ctx.addIssue({ code: 'custom', path: ['fields', index, 'code'], message: `Duplicate field code "${f.code}"` });
      codes.add(f.code);
      if ((f.type === 'SELECT' || f.type === 'MULTI_SELECT') && (!f.options || f.options.length === 0)) {
        ctx.addIssue({ code: 'custom', path: ['fields', index, 'options'], message: `${f.type} field "${f.code}" needs options` });
      }
    });
    const actions = new Set<string>();
    def.actions.forEach((a, index) => {
      if (actions.has(a.code)) ctx.addIssue({ code: 'custom', path: ['actions', index, 'code'], message: `Duplicate action code "${a.code}"` });
      actions.add(a.code);
    });
  });
export type FormDefinition = z.infer<typeof formDefinitionSchema>;
